import { StatusComponent } from '../all-screenings/component/StatusComponent';
import TableCellFilted from '../common/componentUI/TableCellFilted';
import queueClosureDispatcher from './action';
import GroupToolbar from './component/GroupToolbar';
import ModalCreateEditClosure from './component/ModalCreateEditClosure';
import {
  COLUMN_QUEUE_CLOSURE,
  QUEUE_CLOSURE_FILTER,
  QUEUE_CLOSURE_REASONS,
  QUEUE_CLOSURE_STATUS,
  QUEUE_CLOSURE_STATUS_COLOR,
  REASON_STATUS,
  TYPE_MODAL,
} from './constants';
import {
  CloseCircle,
  CreateNewIcon,
  EditIcon,
  EyeIcon,
  NoDataQueueClosureIcon,
} from '@/assets/svg';
import { handleSelectAll } from '@/helpers';
import CustomMenu from '@/new-components/CustomMenu';
import CustomMenuActions from '@/new-components/CustomMenuActions';
import ModalConfirmation from '@/new-components/CustomModal/ModalConfirmation';
import customToast from '@/new-components/CustomNotification';
import CustomPaperContainer from '@/new-components/CustomPaperContainer';
import CustomHeader from '@/new-components/CustomPaperContainer/CustomHeader';
import CustomTable from '@/new-components/CustomTable';
import CustomTooltip from '@/new-components/CustomTooltip';
import {
  Button,
  Drawer,
  IconButton,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { MoreVertRounded } from '@material-ui/icons';
import { debounce, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

const QueueClosure = () => {
  const queryParams = new URLSearchParams(useLocation().search);
  const closureId = queryParams.get('closureId');

  const { list: data, paging } = useSelector((state) => state.queueClosure);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [typeModal, setTypeModal] = useState(null);

  const [sortOption, setSortOption] = useState({});
  const [searchKey, setSearchKey] = useState('');

  const [locationIds, setLocationIds] = useState([]);
  const [closureStatuses, setClosureStatuses] = useState([]);
  const [appointmentDate, setAppointmentDate] = useState([]);
  const [closureReasons, setClosureReasons] = useState([]);

  const [filterOption, setFilterOption] = useState({});

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const onFilterOptionChange = (key) => (e, newValue) => {
    let value = e.target.value;

    switch (key) {
      case 'statuses':
        value = handleSelectAll(
          value,
          QUEUE_CLOSURE_FILTER.map((item) => item.key),
          closureStatuses
        );
        setClosureStatuses(value);
        break;
      case 'reasons':
        value = handleSelectAll(
          value,
          QUEUE_CLOSURE_REASONS.map((item) => item.key),
          closureReasons
        );
        setClosureReasons(value);
        break;

      default:
        break;
    }

    setFilterOption({
      ...filterOption,
      [key]: value,
    });
  };

  const onStopQueueClosure = () => {
    if (isEmpty(selectedItem)) return;

    queueClosureDispatcher.stopQueueClosure(selectedItem.id, () => {
      customToast(
        'success',
        'Queue closure request has been updated successfully.',
        'Queue Closure'
      );
      fetchData();
      setShowCancelModal(false);
      onCloseModal();
    });
  };

  const onResetFilterOps = () => {
    setLocationIds([]);
    setClosureStatuses([]);
    setFilterOption({});
    setAppointmentDate([]);
    setClosureReasons([]);
  };

  const onShowModal = (type) => {
    setShowModal(true);
    setTypeModal(type);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setTypeModal(null);
  };

  const handleSubmitQueueClosure = (type, data) => {
    setLoading(true);
    if (type === TYPE_MODAL.Create) {
      queueClosureDispatcher.createQueueClosure(
        data,
        () => {
          customToast(
            'success',
            'Queue closure request has been created successfully.',
            'Queue Closure'
          );
          fetchData();
          onCloseModal();
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      );
    } else {
      queueClosureDispatcher.editQueueClosure(
        selectedItem.id,
        data,
        () => {
          customToast(
            'success',
            'Queue closure request has been updated successfully.',
            'Queue Closure'
          );
          fetchData();
          onCloseModal();
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      );
    }
  };

  const fetchData = async (
    search = searchKey,
    pagingData = paging,
    sortOptionData = sortOption,
    filterOptionData = filterOption
  ) => {
    queueClosureDispatcher.getData(
      search,
      pagingData,
      sortOptionData,
      filterOptionData
    );
  };

  const debounceLoadData = useCallback(
    debounce((searchKey, paging, sortOption, filterOption) => {
      fetchData(
        searchKey,
        {
          pageIndex: 0,
          pageSize: paging.pageSize,
        },
        sortOption,
        filterOption
      );
    }, 500),
    []
  );

  useEffect(() => {
    debounceLoadData(searchKey, paging, sortOption, filterOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, sortOption, filterOption]);

  useEffect(() => {
    return () => {
      queueClosureDispatcher.resetData();
    };
  }, []);

  useEffect(() => {
    setSearchKey(closureId);
  }, [closureId]);

  const TableHeader = () => (
    <TableHead>
      <TableRow>
        {COLUMN_QUEUE_CLOSURE.map((item) =>
          item.sortBy ? (
            <TableCellFilted
              key={item.stateValue}
              label={item.label}
              stateValue={item.stateValue}
              sortBy="Date"
              sortOption={sortOption}
              onSortChange={() => {
                let newSortOption = {
                  sortBy: 'BookedApptDate',
                  orderBy: sortOption.orderBy !== 'Asc' ? 'Asc' : 'Desc',
                };
                setSortOption(newSortOption);
              }}
              style={{
                minWidth: item.minWidth || 'unset',
                width: item.width || 'unset',
              }}
              isHidden={false}
            />
          ) : (
            <TableCellFilted
              key={item.stateValue}
              label={item.label}
              stateValue={item.stateValue}
              style={{
                minWidth: item.minWidth || 'unset',
                width: item.width || 'unset',
              }}
              isHidden={false}
            />
          )
        )}
      </TableRow>
    </TableHead>
  );

  const renderDateTime = (row) => {
    let startDateFormat, startTimeFormat;
    const { creationTime } = row;

    if (!creationTime) return null;

    startDateFormat = moment(creationTime).format('DD/MM/YYYY');
    startTimeFormat = moment(creationTime).format('LT');
    return `${startDateFormat} ${startTimeFormat}`;
  };

  const renderTableBody = (row) => {
    return (
      <>
        <TableCell>
          <CustomTooltip content={row.queueClosureShortId || '—'} />
        </TableCell>
        <TableCell>
          <CustomTooltip content={REASON_STATUS[row.reason] || '—'} />
        </TableCell>
        <TableCell>
          <CustomTooltip content={row.locationName || '—'} />
        </TableCell>

        <TableCell>
          {row.startDate ? moment(row.startDate).format('DD/MM/YYYY') : '—'}
        </TableCell>
        <TableCell>
          {row.endDate ? moment(row.endDate).format('DD/MM/YYYY') : '—'}
        </TableCell>
        <TableCell>
          {row.startDate
            ? moment(row.startTime, 'HH:mm:ss').format('hh:mm A')
            : '—'}
        </TableCell>
        <TableCell>
          {row.endDate
            ? moment(row.endTime, 'HH:mm:ss').format('hh:mm A')
            : '—'}
        </TableCell>

        <TableCell>
          <CustomTooltip content={row.requestedBy || '—'} />
        </TableCell>

        <TableCell>{renderDateTime(row) || '—'}</TableCell>

        <TableCell>
          <StatusComponent
            filterList={QUEUE_CLOSURE_STATUS}
            filterListColor={QUEUE_CLOSURE_STATUS_COLOR}
            status={row.status}
          />
        </TableCell>
        <TableCell>
          <IconButton
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setSelectedItem(row);
            }}
          >
            <MoreVertRounded />
          </IconButton>
        </TableCell>
      </>
    );
  };

  let listActions = [
    {
      key: 'edit',
      icon: EditIcon,
      label: 'Edit queue closure',
      styles: { width: 20, height: 20 },
      onClick: () => {
        setAnchorEl(null);
        setShowModal(true);
        onShowModal(TYPE_MODAL.Edit);
      },
    },
    {
      key: 'cancel',
      icon: CloseCircle,
      label: 'Stop queue closure',
      color: '#DB3D49',
      styles: { width: 18, height: 18 },
      onClick: () => {
        setAnchorEl(null);
        setShowCancelModal(true);
      },
    },
  ];

  const renderListActions = () => {
    if (!selectedItem) return listActions;
    if (
      [QUEUE_CLOSURE_STATUS.Cancelled, QUEUE_CLOSURE_STATUS.Expired].includes(
        selectedItem.status
      )
    ) {
      return [
        {
          key: 'view',
          icon: EyeIcon,
          label: 'View details',
          onClick: () => {
            setAnchorEl(null);
            setShowModal(true);
            onShowModal(TYPE_MODAL.View);
          },
        },
      ];
    }
    return listActions;
  };
  return (
    <>
      <CustomMenu
        id="simple-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <CustomMenuActions listActions={renderListActions()} />
      </CustomMenu>
      <CustomPaperContainer
        header={
          <CustomHeader
            searchPlaceholder="Search by Closure's ID..."
            title="Queue Closure"
            setSearchKey={setSearchKey}
            searchKey={searchKey}
            filterOption={filterOption}
            setFilterOption={setFilterOption}
            sortOption={sortOption}
            fetchData={fetchData}
            renderToolbar={() => (
              <GroupToolbar
                filterOption={filterOption}
                setFilterOption={setFilterOption}
                onFilterOptionChange={onFilterOptionChange}
                onResetFilterOps={onResetFilterOps}
                locationIds={locationIds}
                setLocationIds={setLocationIds}
                closureStatuses={closureStatuses}
                appointmentDate={appointmentDate}
                setAppointmentDate={setAppointmentDate}
                closureReasons={closureReasons}
              />
            )}
            renderButton={() => (
              <Button
                color="primary"
                variant="contained"
                startIcon={<CreateNewIcon />}
                onClick={() => {
                  setSelectedItem(null);
                  onShowModal(TYPE_MODAL.Create);
                }}
              >
                Create Request
              </Button>
            )}
          />
        }
      >
        <CustomTable
          data={data}
          paging={paging}
          header={TableHeader}
          renderRow={(row, i) => renderTableBody(row)}
          noDataHelperText="No queue closure available"
          noDataHelperSubText="Please come back at another time."
          Icon={NoDataQueueClosureIcon}
          totalCols={COLUMN_QUEUE_CLOSURE.length}
          fetchData={fetchData}
        />

        <Drawer
          anchor="right"
          open={showModal}
          onClose={onCloseModal}
          disableBackdropClick
          PaperProps={{
            style: {
              width: '80%',
              minWidth: 720,
            },
          }}
        >
          <ModalCreateEditClosure
            setShowCancelModal={setShowCancelModal}
            onSubmit={handleSubmitQueueClosure}
            selectedItem={selectedItem}
            typeModal={typeModal}
            onClose={() => {
              setShowModal(false);
              fetchData();
            }}
          />
        </Drawer>

        {showCancelModal && (
          <ModalConfirmation
            open={showCancelModal}
            onClick={onStopQueueClosure}
            onClose={() => setShowCancelModal(false)}
            mainContent={'Stop queue closure'}
            subContent={'Has management been informed?'}
            cancelLabel="No"
            confirmLabel="Yes"
          />
        )}
      </CustomPaperContainer>
    </>
  );
};

export default QueueClosure;
